import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Film, Loader2, Maximize2 } from 'lucide-react';

import EmptyState from '@/components/ui/EmptyState';
import Lightbox from '@/components/ui/Lightbox';
import SectionHeader from '@/components/ui/SectionHeader';
import InstructButton from '@/components/production/tiles/InstructButton';
import IteratingChip from '@/components/production/tiles/IteratingChip';
import RejectedSection from '@/components/production/tiles/RejectedSection';
import ReworkPendingOverlay from '@/components/production/tiles/ReworkPendingOverlay';
import RollBadge from '@/components/production/tiles/RollBadge';
import ShotReferencesStrip from '@/components/production/tiles/ShotReferencesStrip';
import { useProductionContext, useTileAspect } from '@/context/ProductionContext';
import { useShotBoard } from '@/hooks/useShotBoard';
import { bustSrc, normKey } from '@/lib/assetKeys';
import { cn } from '@/lib/utils';

/** Hover-to-preview handlers: play muted on enter, rewind on leave. */
const hoverPlay = {
  onMouseEnter: (e: React.MouseEvent<HTMLVideoElement>) => void (e.target as HTMLVideoElement).play().catch(() => {}),
  onMouseLeave: (e: React.MouseEvent<HTMLVideoElement>) => {
    const v = e.target as HTMLVideoElement;
    v.pause();
    v.currentTime = 0;
  },
};

/** The dailies: one rendered video clip per planned shot. */
export default function ClipsGallery() {
  const { t } = useTranslation(['production', 'common']);
  const { assets, shotPlan, productionId, pendingRework, finalAssets, running, reworkBust, vertical, storyboardSheet } =
    useProductionContext();
  const tileAspect = useTileAspect();
  const { items, rejected, metaById, byShot, displayIds, failedSrcs, markFailed } = useShotBoard(assets, shotPlan, 'clip');
  const [active, setActive] = useState<string | null>(null);
  /** A rejected take opened full-size for review (keyed by shot_id). */
  const [rejectView, setRejectView] = useState<string | null>(null);

  if (displayIds.length === 0) {
    return (
      <EmptyState title={t('dailies.emptyTitle')}>
        {t('dailies.empty')} {t('dailies.help')}
      </EmptyState>
    );
  }

  const step = (dir: -1 | 1) => {
    const idx = items.findIndex((c) => c.shot_id === active);
    const next = idx + dir;
    if (idx >= 0 && next >= 0 && next < items.length) setActive(items[next].shot_id);
  };

  return (
    <div>
      <SectionHeader
        title={t('step.dailies')}
        help={t('dailies.help')}
        subtitle={t('dailies.rendered', { done: items.length, total: displayIds.length })}
      />

      <div
        className={cn(
          'grid gap-3',
          vertical ? 'grid-cols-[repeat(auto-fill,minmax(150px,1fr))]' : 'grid-cols-[repeat(auto-fill,minmax(260px,1fr))]',
        )}
      >
        {displayIds.map((shotId) => {
          const clip = byShot.get(shotId);
          const m = metaById[shotId];
          const missing = !clip || failedSrcs.has(clip.path);

          if (missing) {
            return (
              <div
                key={shotId}
                className={cn(
                  'relative flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/30',
                  tileAspect,
                )}
              >
                <Loader2 className="h-4 w-4 animate-spin text-faint" />
                {m && <RollBadge roll={m.roll} label={m.rollLabel} dim />}
                <div
                  className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1
                             truncate px-1 py-1 font-mono text-[10px] text-faint"
                >
                  <Film className="h-3 w-3" />
                  {shotId} · {clip ? t('state.rendering', { ns: 'common' }) : t('state.queued', { ns: 'common' })}
                </div>
              </div>
            );
          }

          // Footage dailies land as an asset_ready of type "reframe" (the QC gate),
          // not "clip" — without checking both, the ITERATING chip flashed forever on
          // locked, approved footage clips while the run continued.
          const iterating =
            running && !finalAssets.has(`clip:${normKey(shotId)}`) && !finalAssets.has(`reframe:${normKey(shotId)}`);

          return (
            <div key={shotId} className="group relative cursor-pointer" onClick={() => setActive(shotId)}>
              <video
                src={bustSrc(clip.path, reworkBust[normKey(shotId)])}
                className={cn(
                  'w-full rounded-lg border border-border bg-black object-cover transition-colors group-hover:border-primary/60',
                  tileAspect,
                )}
                muted
                playsInline
                // Grid tiles only need the first frame; the full clip loads on play.
                preload="metadata"
                onError={() => markFailed(clip.path)}
                {...hoverPlay}
              />
              {m && <RollBadge roll={m.roll} label={m.rollLabel} />}
              <div
                className="absolute inset-x-0 bottom-0 flex items-center gap-1 truncate rounded-b
                           bg-black/60 px-1.5 py-1 font-mono text-[10px] text-white"
              >
                <Film className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  {shotId}
                  {m ? ` · ${m.shotType} · ${m.rollLabel}` : ''}
                </span>
              </div>
              {/* Hidden while the clip is still iterating or already has a queued
                  rework — matches the storyboard, so producers only request changes on
                  a settled daily. */}
              {!iterating && !pendingRework.has(normKey(shotId)) && (
                <div className="absolute left-1 top-1 z-20 opacity-0 transition-opacity group-hover:opacity-100">
                  <InstructButton productionId={productionId} assetRef={`clip:${shotId}`} label={shotId} />
                </div>
              )}
              {pendingRework.has(normKey(shotId)) && <ReworkPendingOverlay />}
              {iterating && <IteratingChip />}
            </div>
          );
        })}
      </div>

      <RejectedSection title={t('dailies.rejected')} count={rejected.length} note={t('dailies.rejectedNote')}>
        {/* Bigger tiles with real controls and audio, so a producer can actually watch
            a reject rather than hover-preview it muted. preload="metadata" shows a
            poster frame instead of a black tile. */}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
          {rejected.map((c) => (
            <div key={c.shot_id} className="relative">
              <div className="relative">
                <video
                  src={c.path}
                  className={cn('w-full rounded border border-border bg-black', tileAspect)}
                  playsInline
                  preload="metadata"
                  controls
                />
                <button
                  type="button"
                  onClick={() => setRejectView(c.shot_id)}
                  title={t('dailies.watchFullSize')}
                  aria-label={t('dailies.watchFullSize')}
                  className="absolute right-1 top-1 rounded bg-black/70 p-1 text-white hover:bg-black/90"
                >
                  <Maximize2 className="h-3 w-3" />
                </button>
              </div>
              <div className="flex items-center justify-between gap-1">
                <span className="truncate font-mono text-[10px] text-faint">{c.shot_id}</span>
                <a href={c.path} download className="shrink-0 text-[10px] text-primary hover:underline">
                  {t('action.download', { ns: 'common' })}
                </a>
              </div>
            </div>
          ))}
        </div>
      </RejectedSection>

      {rejectView && (
        <Lightbox label={t('dailies.rejectedTakeLabel', { id: rejectView })} onClose={() => setRejectView(null)}>
          <video
            src={rejected.find((c) => c.shot_id === rejectView)?.path}
            controls
            autoPlay
            className="mx-auto max-h-[78vh] w-auto max-w-full rounded-lg"
          />
        </Lightbox>
      )}

      {active && (
        <Lightbox
          label={active}
          onClose={() => setActive(null)}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
          footer={
            <ShotReferencesStrip
              shotId={active}
              productionId={productionId}
              shotPlan={shotPlan}
              assets={assets}
              startFrame={storyboardSheet ? 'sheet' : 'frame'}
            />
          }
        >
          <video
            src={items.find((c) => c.shot_id === active)?.path}
            controls
            autoPlay
            className={cn('mx-auto max-h-[78vh] w-auto max-w-full rounded-lg')}
          />
        </Lightbox>
      )}
    </div>
  );
}
